<template>
  <div class="upload-page">
    <div class="viva-container">
      
      <!-- Loading State -->
      <div v-if="loading" class="viva-section">
        <div class="viva-spinner-section">
          <i class="fa fa-spinner fa-spin viva-spinner"></i>
          <p class="viva-spinner-text">Setting up your payment...</p>
        </div>
      </div>
      
      <!-- Error State -->
      <div v-if="error" class="viva-section">
        <div class="alert alert-danger">
          <h4>Payment Setup Error</h4>
          <p>{{ error }}</p>
          <div class="error-actions">
            <button class="viva-btn viva-btn-orange" @click="retrySetup">
              Try Again
            </button>
          </div>
        </div>
      </div>
      
      <!-- Payment Form (Shell) -->
      <div v-show="!loading && !error && cartId" class="viva-section">
        
        <!-- Payment Methods -->
        <div class="viva-modal-backdrop">
          <div class="viva-modal-content">
            <div class="viva-modal-header">
              <h2>Complete Payment</h2>
            </div>

            <div class="viva-modal-body">
              <div class="price-display">
                <h3>VIVA Fee: ${{  amount }}</h3>
                <p>One-time payment allows you to send your video to unlimited schools</p>
              </div>

              <!-- Fee Waiver Link -->
              <div v-if="showFeeWaiver" class="fee-waiver-section">
                <p class="text-link">
                  Do you qualify for a fee waiver?
                  <a data-cy="payment-goToWaiver" href="#" @click.prevent="showWaiverModal = true" class="link-accent">Click here to find out</a>
                </p>
              </div>

              <!-- Grad applicants don't qualify for the self-serve waiver flow above (its
                   questions are US-high-school-specific), so point them to email instead -->
              <div v-if="applicantType === 'graduate'" class="fee-waiver-section">
                <p class="text-link" data-cy="payment-gradWaiverNote">
                  <strong>Need help with the fee?</strong> You may qualify for a waiver.
                  Email us at <a href="mailto:viva@initialview.com" class="link-accent">viva@initialview.com</a>.
                </p>
              </div>

              <!-- Stripe Elements Container -->
              <div id="payment-element" v-show="paymentReady" class="payment-element-container"></div>
              
              <!-- Loading state while Stripe initializes -->
              <div v-if="!paymentReady && !error" class="payment-loading">
                <i class="fa fa-spinner fa-spin viva-spinner-small"></i>
                Setting up secure payment...
              </div>
              
              <!-- Payment button -->
              <button
                data-cy="payment-submit"
                class="viva-btn viva-btn-green viva-btn-block payment-submit-btn"
                @click="handlePayment"
                :disabled="!paymentReady || processing"
              >
                <span v-if="processing">
                  <i class="fa fa-spinner fa-spin viva-spinner-small"></i> Processing Payment...
                </span>
                <span v-else>
                  Complete Payment - ${{ amount }}
                </span>
              </button>
            </div>
          </div>
        </div>
        
      </div>

      <!-- Fee Waiver Modal -->
      <div v-if="showWaiverModal" class="viva-modal-backdrop">
        <div class="viva-modal-content waiver-modal">
          <div class="viva-modal-header">
            <h2>Fee Waiver Eligibility</h2>
          </div>

          <div class="viva-modal-body">
            <p class="waiver-intro">
              Do any of these situations apply to you?
            </p>

            <div class="waiver-questions">
              <!-- Question Headers -->
              <div class="question-headers">
                <span class="header-spacer"></span>
                <span class="answer-header">Yes</span>
                <span class="answer-header">No</span>
              </div>

              <!-- Question 1: Free/Reduced Lunch -->
              <div class="waiver-question">
                <label class="question-text">
                  Are you enrolled in a free or reduced-price meal program at school?
                </label>
                <div class="radio-group">
                  <input
                    data-cy="paymentWaiver-question1-yes"
                    type="radio"
                    name="mealProgram"
                    value="yes"
                    v-model="waiverAnswers.mealProgram"
                    class="waiver-radio"
                  />
                  <input
                    data-cy="paymentWaiver-question1-no"
                    type="radio"
                    name="mealProgram"
                    value="no"
                    v-model="waiverAnswers.mealProgram"
                    class="waiver-radio"
                  />
                </div>
              </div>

              <!-- Question 2: Fee Waiver Programs -->
              <div class="waiver-question">
                <label class="question-text">
                  Do you qualify for a fee waiver through
                  <a href="https://www.act.org/content/dam/act/unsecured/documents/FeeWaiver.pdf" target="_blank">ACT</a>,
                  <a href="https://collegereadiness.collegeboard.org/sat/register/fees/fee-waivers" target="_blank">College Board</a>,
                  and/or <a href="https://www.nacacfairs.org/learn/fee-waiver/frequently-asked-questions/" target="_blank">NACAC</a>?
                </label>
                <div class="radio-group">
                  <input
                    data-cy="paymentWaiver-question2-yes"
                    type="radio"
                    name="feeWaiver"
                    value="yes"
                    v-model="waiverAnswers.feeWaiver"
                    class="waiver-radio"
                  />
                  <input
                    data-cy="paymentWaiver-question2-no"
                    type="radio"
                    name="feeWaiver"
                    value="no"
                    v-model="waiverAnswers.feeWaiver"
                    class="waiver-radio"
                  />
                </div>
              </div>

              <!-- Question 3: Community Organization -->
              <div class="waiver-question">
                <label class="question-text">
                  Are you a part of a Community Based Organization (like QuestBridge or Prep for Prep) that is focused on college access?
                </label>
                <div class="radio-group">
                  <input
                    data-cy="paymentWaiver-question3-yes"
                    type="radio"
                    name="communityOrg"
                    value="yes"
                    v-model="waiverAnswers.communityOrg"
                    class="waiver-radio"
                  />
                  <input
                    data-cy="paymentWaiver-question3-no"
                    type="radio"
                    name="communityOrg"
                    value="no"
                    v-model="waiverAnswers.communityOrg"
                    class="waiver-radio"
                  />
                </div>
              </div>

              <!-- Question 4: Armed Forces -->
              <div class="waiver-question">
                <label class="question-text">
                  Are you a current or former member of the US Armed Forces?
                </label>
                <div class="radio-group">
                  <input
                    data-cy="paymentWaiver-question4-yes"
                    type="radio"
                    name="armedForces"
                    value="yes"
                    v-model="waiverAnswers.armedForces"
                    class="waiver-radio"
                  />
                  <input
                    data-cy="paymentWaiver-question4-no"
                    type="radio"
                    name="armedForces"
                    value="no"
                    v-model="waiverAnswers.armedForces"
                    class="waiver-radio"
                  />
                </div>
              </div>
            </div>

            <div>
              <p class="text-link">If you think you might qualify for a fee waiver for reasons other than those listed here, please email us at <a class="link-accent" href="mailto:viva@initialview.com">viva@initialview.com</a>.</p>
            </div>

            <div class="waiver-actions">
              <button
                data-cy="paymentWaiver-close"
                class="viva-btn viva-btn-grey"
                @click="closeWaiverModal"
              >
                Cancel
              </button>
              <button
                data-cy="paymentWaiver-submit"
                class="viva-btn viva-btn-green"
                @click="submitWaiverForm"
                :disabled="!allQuestionsAnswered"
              >
                Submit
              </button>
            </div>
          </div>
        </div>
      </div>

      <!-- Back Button -->
      <div class="back-section">
        <router-link to="/upload" class="back-link">
          <i class="fa fa-arrow-left"></i> Back
        </router-link>
      </div>

    </div>
  </div>
</template>

<script>
import { loadStripe } from '@stripe/stripe-js'
import apiClient from '../utils/apiClient.js'

export default {
  name: 'Payment',
  data() {
    return {
      loading: true,
      error: null,
      cartId: null,
      amount: null,
      examId: null,
      applicantType: null,
      processing: false,

      // Stripe-related properties
      stripe: null,
      elements: null,
      paymentElement: null,
      paymentReady: false,
      paymentDetails: {
        paymentIntent: null,
        ephemeralKey: null,
        customerId: null,
      },

      // Fee waiver modal
      showWaiverModal: false,
      waiverAnswers: {
        mealProgram: null,
        feeWaiver: null,
        communityOrg: null,
        armedForces: null
      }
    }
  },

  computed: {
    allQuestionsAnswered() {
      return this.waiverAnswers.mealProgram !== null &&
             this.waiverAnswers.feeWaiver !== null &&
             this.waiverAnswers.communityOrg !== null &&
             this.waiverAnswers.armedForces !== null
    },
    // All four VivaFeeWaiver reasons (free/reduced lunch, ACT/CollegeBoard/NACAC fee
    // waiver, community-based org, armed forces) are US high-school programs, so the
    // step doesn't apply to graduate or transfer applicants. Boarding/day-school
    // applicants can still be domestic high schoolers, so they keep it.
    showFeeWaiver() {
      return this.applicantType !== 'graduate' && this.applicantType !== 'transfer'
    }
  },

  async mounted() {
    await this.setupPayment()
  },

  methods: {
    async setupPayment() {
      try {
        this.loading = true
        this.error = null

        // Get vparam from URL or sessionStorage
        const vparam = this.$route.query.vparam || sessionStorage.getItem('vparam')

        // Step 1: Create cart for this viva
        const cartResponse = await apiClient.post('/api/viva/create_payment_cart/', {
          vparam: vparam
        })
        
        if (cartResponse.data.status === 200) {
          this.cartId = cartResponse.data.cart_id
          this.amount = cartResponse.data.amount
          this.examId = cartResponse.data.exam_id
          this.applicantType = cartResponse.data.applicant_type
          
          console.log('Payment cart created:', cartResponse.data)
          
          // Step 2: Initialize Stripe payment
          if (this.amount > 0) {
            await this.initializeStripe()
          } else {
            // Amount is $0.00 - skip Stripe and redirect to practice
            console.log('Amount is $0.00, skipping payment')
            await apiClient.get(`/api/interview/payment/stripe/mobile?cid=${this.cartId}`)
            this.$router.push('/practice')
            return
          }
          
        } else {
          throw new Error(cartResponse.data.error || 'Failed to create payment cart')
        }
        
      } catch (error) {
        console.error('Payment setup failed:', error)
        
        if (error.response && error.response.data) {
          this.error = error.response.data.error || 'Payment setup failed'
          
          // Handle different error scenarios with intelligent redirects
          if (error.response.status === 400 || error.response.status === 404) {
            // User doesn't have proper status or exam, check their actual status
            this.checkStatusAndRedirect()
            return
          }
        } else {
          this.error = 'Unable to set up payment. Please try again.'
        }
        
      } finally {
        this.loading = false
      }
    },
    
    async checkStatusAndRedirect() {
      try {
        const statusResponse = await apiClient.get('/api/viva/fetch_status/')
        const status = statusResponse.data.student_status
        
        console.log('User status:', status)
        
        // Redirect based on actual status
        switch (status) {
          case 'ready_to_upload':
            this.$router.push('/upload')
            break
          case 'ready_for_exam':
            this.$router.push('/practice')
            break
          case 'ready_to_review':
            this.$router.push('/send')
            break
          case 'ready_to_pay':
            // They should be able to pay, but something went wrong
            this.error = 'Unable to set up payment. Please try refreshing the page.'
            break
          default:
            // Unknown status, send to upload
            this.$router.push('/upload')
        }
      } catch (statusError) {
        console.error('Error checking status:', statusError)
        // If we can't check status, default to upload
        setTimeout(() => {
          this.$router.push('/upload')
        }, 3000)
      }
    },
    
    async retrySetup() {
      await this.setupPayment()
    },
    
    async initializeStripe() {
      try {
        // Get Stripe public key from existing Glimpse endpoint
        const pubkeyResponse = await apiClient.get('/api/interview/payment/stripe/pubkey')
        const publicKey = pubkeyResponse.data.publicKey
        
        // Initialize Stripe
        this.stripe = await loadStripe(publicKey)
        
        // Get PaymentIntent from existing Glimpse mobile endpoint
        const paymentResponse = await apiClient.get(`/api/interview/payment/stripe/mobile?cid=${this.cartId}`)
        
        this.paymentDetails = {
          paymentIntent: paymentResponse.data.paymentIntent, // client_secret
          ephemeralKey: paymentResponse.data.ephemeralKey,
          customerId: paymentResponse.data.customer,
        }
        
        // Configure Stripe Elements with Viva branding
        const appearance = {
          theme: 'stripe',
          variables: {
            colorPrimary: '#80C297', // var(--viva-green)
            colorBackground: '#f8f9fa',
            colorText: '#333333', // var(--viva-dark)
            fontFamily: 'poppins, sans-serif',
            borderRadius: '12px',
          },
        }
        
        const options = {
          clientSecret: this.paymentDetails.paymentIntent,
          appearance: appearance,
        }
        
        // Create and mount payment element
        this.elements = this.stripe.elements(options)
        this.paymentElement = this.elements.create('payment')
        this.paymentElement.mount('#payment-element')
        
        this.paymentReady = true
        console.log('Stripe payment setup complete')
        
      } catch (error) {
        console.error('Stripe initialization failed:', error)
        throw error
      }
    },
    
    async handlePayment() {
      if (!this.stripe || !this.elements || this.processing) return
      
      try {
        this.processing = true
        
        // Submit the form (validates input)
        const { error: submitError } = await this.elements.submit()
        if (submitError) {
          throw submitError
        }
        
        // Confirm payment with Stripe
        const result = await this.stripe.confirmPayment({
          elements: this.elements,
          clientSecret: this.paymentDetails.paymentIntent,
          confirmParams: {
            return_url: `${window.location.origin}/viva/practice`, // Fallback URL
          },
          redirect: 'if_required', // Only redirect if 3D Secure required
        })
        
        if (result.error) {
          // Payment failed - handle error inline
          console.error('Payment failed:', result.error)
          let message = 'Payment failed. Please try again.'
          
          switch (result.error.type) {
            case 'card_error':
              message = result.error.message || 'Your card was declined.'
              break
            case 'validation_error':
              message = 'Please check your payment information.'
              break
            case 'authentication_error':
              message = 'Payment requires additional authentication.'
              break
            default:
              message = result.error.message || message
          }
          
          alert(message) // TODO: Replace with proper error UI
        } else {
          // Payment succeeded - verify and redirect inline
          console.log('Payment succeeded:', result.paymentIntent)
          
          try {
            // Use existing Glimpse payment verification endpoint
            const response = await apiClient.post('/api/interview/payment/stripe/paid', {
              cid: this.cartId
            })
            
            if (response.status === 200 && response.data.cart_status === 2) {
              console.log('Payment verification successful')
              
              // Redirect to practice page
              this.$router.push('/practice')
            } else {
              throw new Error('Payment verification failed')
            }
            
          } catch (verifyError) {
            console.error('Payment verification error:', verifyError)
            alert('Payment completed but verification failed. Please contact support if you were charged.')
          }
        }
        
      } catch (error) {
        console.error('Payment processing error:', error)
        
        let message = 'Payment failed. Please try again.'
        switch (error.type) {
          case 'card_error':
            message = error.message || 'Your card was declined.'
            break
          case 'validation_error':
            message = 'Please check your payment information.'
            break
          case 'authentication_error':
            message = 'Payment requires additional authentication.'
            break
          default:
            message = error.message || message
        }
        
        alert(message) // TODO: Replace with proper error UI
      } finally {
        this.processing = false
      }
    },

    // Fee waiver methods
    closeWaiverModal() {
      this.showWaiverModal = false
      this.resetWaiverForm()
    },

    resetWaiverForm() {
      this.waiverAnswers = {
        mealProgram: null,
        feeWaiver: null,
        communityOrg: null,
        armedForces: null
      }
    },

    async submitWaiverForm() {
      if (!this.allQuestionsAnswered) return

      try {
        // Check if any answer is "yes"
        const hasYesAnswer = Object.values(this.waiverAnswers).includes('yes')

        if (hasYesAnswer) {
          // Apply fee waiver
          const reason = this.getWaiverReason()
          const vparam = this.$route.query.vparam || sessionStorage.getItem('vparam')

          const response = await apiClient.post('/api/viva/apply_fee_waiver/', {
            reason: reason,
            vparam: vparam
          })

          if (response.data.status === 200) {
            console.log('Fee waiver applied successfully')
            this.closeWaiverModal()

            // Refresh payment setup to get $0.00 amount
            await this.setupPayment()
          } else {
            throw new Error(response.data.error || 'Failed to apply fee waiver')
          }
        } else {
          // No waiver eligibility, just close modal
          this.closeWaiverModal()
        }

      } catch (error) {
        console.error('Fee waiver application failed:', error)
        alert('Failed to apply fee waiver. Please try again.')
      }
    },

    getWaiverReason() {
      // Return the first "yes" answer as the reason
      if (this.waiverAnswers.mealProgram === 'yes') return 'free_lunch'
      if (this.waiverAnswers.feeWaiver === 'yes') return 'fee_waiver'
      if (this.waiverAnswers.communityOrg === 'yes') return 'community_org'
      if (this.waiverAnswers.armedForces === 'yes') return 'armed_forces'
      return ''
    }
  }
}
</script>

<style scoped>
@import '../styles/viva-classes.css';


.price-display {
  margin-bottom: 25px;
  padding: 20px;
  background: var(--viva-cream);
  border-radius: 8px;
  border: 2px solid var(--viva-green);
  text-align: center;
}

.price-display h3 {
  margin: 0 0 10px 0;
  color: var(--viva-dark);
  font-size: 24px;
  font-weight: 600;
}

.price-display p {
  margin: 0;
  color: var(--viva-grey);
  font-size: 14px;
}

.payment-element-container {
  margin: 20px 0;
  padding: 20px;
  border: 1px solid #dee2e6;
  border-radius: 8px;
  background: #f8f9fa;
  text-align: left;
}

.payment-loading {
  text-align: center;
  padding: 40px 20px;
  color: var(--viva-grey);
}

.payment-submit-btn {
  margin-top: 20px;
}

.error-actions {
  margin-top: 20px;
}

.error-actions button {
  margin-right: 10px;
}

/* Fee Waiver Styles */
.fee-waiver-section {
  margin: 15px 0;
  padding: 15px;
  background: #f8f9fa;
  border-radius: 6px;
  text-align: center;
}

.waiver-modal {
  max-width: 600px;
  max-height: 80vh;
  overflow-y: auto;
}

.modal-close-btn {
  background: none;
  border: none;
  font-size: 18px;
  color: var(--viva-grey);
  cursor: pointer;
  padding: 5px;
  border-radius: 4px;
}

.modal-close-btn:hover {
  background: #f0f0f0;
  color: var(--viva-dark);
}

.waiver-intro {
  font-size: 16px;
  font-weight: 600;
  color: var(--viva-dark);
  text-align: center;
}

.waiver-questions {
  margin-bottom: 25px;
}

.question-headers {
  display: flex;
  align-items: center;
  margin-bottom: 15px;
  font-weight: 600;
  color: var(--viva-dark);
}

.header-spacer {
  flex: 1;
}

.answer-header {
  width: 60px;
  text-align: center;
  font-size: 14px;
}

.waiver-question {
  display: flex;
  align-items: flex-start;
  margin-bottom: 20px;
  padding: 15px;
  border: 1px solid #dee2e6;
  border-radius: 6px;
  background: #fff;
}

.question-text {
  flex: 1;
  font-size: 14px;
  line-height: 1.4;
  color: var(--viva-dark);
  margin-right: 15px;
}

.radio-group {
  display: flex;
  gap: 20px;
  align-items: center;
}

.waiver-radio {
  width: 20px;
  height: 20px;
  margin: 0;
  cursor: pointer;
  accent-color: var(--viva-orange);
}

.waiver-actions {
  display: flex;
  justify-content: space-between;
  gap: 15px;
  margin-top: 25px;
}

.waiver-actions button {
  flex: 1;
}

/* Responsive adjustments */
@media (max-width: 768px) {
  .waiver-modal {
    margin: 20px;
    max-width: calc(100% - 40px);
  }

  .waiver-question {
    flex-direction: column;
    align-items: stretch;
  }

  .question-text {
    margin-right: 0;
    margin-bottom: 10px;
  }

  .radio-group {
    justify-content: center;
  }

  .question-headers {
    display: none;
  }
}
</style>

